import { useSubscriptions, useUpdateSubscription } from '../../hooks/useSubscriptions';
import { useToast } from '@/components/ui';
import { SubscriptionRow } from './SubscriptionRow';
import type { EventSubscription } from '../../types';

interface AgentSubscriptionsTabProps {
  agentId: string;
}

export function AgentSubscriptionsTab({ agentId }: AgentSubscriptionsTabProps) {
  const { data, isLoading } = useSubscriptions({ subscriberId: agentId });
  const { success, error: showError } = useToast();
  const updateSubscription = useUpdateSubscription();

  // Only show statically configured subscriptions here. Dynamic subscriptions
  // (e.g. per-run `agent.run.*` triggers) are created at runtime and aren't part
  // of the agent's configuration, so they'd otherwise clutter this tab and
  // disagree with the tab count (which reflects configured subscriptions only).
  const subscriptions = (data?.items ?? []).filter((sub) => sub.scope !== 'dynamic');

  const handleToggle = async (sub: EventSubscription) => {
    const newStatus = sub.status === 'active' ? 'paused' : 'active';
    try {
      await updateSubscription.mutateAsync({
        subscriptionId: sub.subscriptionId,
        status: newStatus,
      });
      success(`Subscription ${newStatus === 'paused' ? 'paused' : 'resumed'}`);
    } catch {
      showError('Failed to update subscription');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (subscriptions.length === 0) {
    return (
      <p className="text-muted-foreground">
        No event subscriptions for this agent.
      </p>
    );
  }

  return (
    <div className="border border-border rounded-lg overflow-hidden divide-y divide-border">
      {subscriptions.map((sub) => (
        <SubscriptionRow
          key={sub.subscriptionId}
          subscription={sub}
          onToggle={handleToggle}
          isPending={updateSubscription.isPending}
        />
      ))}
    </div>
  );
}
